
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Clock, Navigation, Utensils } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import KosherStamp from './KosherStamp';

const Footer: React.FC = () => {
  const { t, language } = useLanguage();
  const [logoError, setLogoError] = useState(false);

  const links = [
    { name: t.nav.home, href: '/' },
    { name: t.nav.menu, href: '/menu' },
    { name: t.nav.story, href: '/story' },
    { name: t.nav.contact, href: '/contact' },
  ];

  return (
    <footer className="bg-[#050403] text-gray-400 border-t border-fait-gold/20 pt-20 pb-10 relative overflow-hidden font-sans">
      
      {/* Background Texture/Gradient */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-fait-gold/40 to-transparent"></div>
      <div className="absolute -left-40 -bottom-40 w-96 h-96 bg-fait-gold/5 rounded-full blur-3xl pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 text-right rtl:text-right ltr:text-left mb-16">
            
            {/* Column 1: Brand */}
            <div className="space-y-6">
                <Link to="/" className="block">
                    {!logoError ? (
                       <img 
                            src="/images/logo.svg" 
                            alt="Yehuda Fait" 
                            className="h-16 w-auto opacity-90 hover:opacity-100 transition-opacity"
                            onError={() => setLogoError(true)}
                       />
                    ) : (
                        <div className="flex items-center gap-2 group">
                            <Utensils className="h-8 w-8 text-fait-gold group-hover:rotate-12 transition-transform duration-300" />
                            <span className="font-serif text-2xl font-bold text-fait-cream tracking-wide group-hover:text-fait-gold transition-colors">
                            {language === 'he' ? 'יהודה פייט' : 'Yehuda Fait'}
                            </span>
                        </div>
                    )}
                </Link>
                <p className="text-gray-500 text-sm leading-relaxed max-w-xs">
                    {t.home.heroSubtitle}.<br/>
                    {t.footer.tagline}
                </p>
                <div className="flex gap-4 pt-2">
                    <a href="https://waze.com/ul?q=Yehuda+Fait+HaYarkon+39+Bnei+Brak" target="_blank" rel="noreferrer" aria-label={t.contact.waze} className="w-10 h-10 rounded-full bg-fait-brown/30 flex items-center justify-center text-fait-gold hover:bg-fait-gold hover:text-fait-dark transition-all duration-300 border border-fait-gold/20">
                        <Navigation size={18} />
                    </a>
                    <a href="tel:0548478033" aria-label="Phone" className="w-10 h-10 rounded-full bg-fait-brown/30 flex items-center justify-center text-fait-gold hover:bg-fait-gold hover:text-fait-dark transition-all duration-300 border border-fait-gold/20">
                        <Phone size={18} />
                    </a>
                </div>
            </div>

            {/* Column 2: Quick Links */}
            <div>
                <h3 className="text-white font-serif font-bold text-xl mb-6">{language === 'he' ? 'ניווט מהיר' : 'Quick Links'}</h3>
                <ul className="space-y-4">
                    {links.map((link) => (
                        <li key={link.href}>
                            <Link 
                                to={link.href} 
                                className="text-gray-400 hover:text-fait-gold transition-colors flex items-center gap-2 group"
                            >
                                <span className="w-1.5 h-1.5 rounded-full bg-fait-gold/50 group-hover:bg-fait-gold transition-colors"></span>
                                {link.name}
                            </Link>
                        </li>
                    ))}
                    <li>
                         <a 
                                href="https://waze.com/ul?q=Yehuda+Fait+HaYarkon+39+Bnei+Brak" 
                                target="_blank" 
                                rel="noreferrer"
                                className="text-gray-400 hover:text-fait-gold transition-colors flex items-center gap-2 group"
                            >
                                <span className="w-1.5 h-1.5 rounded-full bg-fait-gold/50 group-hover:bg-fait-gold transition-colors"></span>
                                {t.contact.waze}
                        </a>
                    </li>
                </ul>
            </div>

            {/* Column 3: Info */}
            <div>
                 <h3 className="text-white font-serif font-bold text-xl mb-6">{language === 'he' ? 'פרטים' : 'Info'}</h3>
                 <ul className="space-y-6 text-sm">
                     <li className="flex items-start gap-3">
                         <MapPin className="shrink-0 text-fait-gold mt-1" size={18} />
                         <div>
                             <span className="block text-gray-300 font-bold">{t.contact.addressText}</span>
                             <span className="text-gray-500">{t.contact.floor}</span>
                         </div>
                     </li>
                     <li className="flex items-start gap-3">
                         <Clock className="shrink-0 text-fait-gold mt-1" size={18} />
                         <div>
                             <span className="block text-gray-300 font-bold">{t.contact.openDay}</span>
                             <span className="text-gray-500">{t.contact.openHours}</span>
                         </div>
                     </li>
                     <li className="flex items-start gap-3">
                         <Phone className="shrink-0 text-fait-gold mt-1" size={18} />
                         <a href="tel:0548478033" className="text-gray-300 font-bold hover:text-white transition-colors">
                             054-847-8033
                         </a>
                     </li>
                 </ul>
            </div>

            {/* Column 4: Kosher - Updated: No background box */}
            <div className="flex flex-col items-center lg:items-start">
                 <h3 className="text-white font-serif font-bold text-xl mb-4 w-full text-right rtl:text-right ltr:text-left">{t.faq.kosherTitle}</h3>
                 <div className="relative group ml-0 rtl:ml-0 ltr:mr-auto">
                    <KosherStamp className="w-64 h-40 text-fait-gold opacity-80 group-hover:opacity-100 transition-opacity duration-300" />
                 </div>
                 <p className="mt-2 text-xs text-gray-500 text-center lg:text-right rtl:lg:text-right ltr:lg:text-left max-w-[200px]">
                     {t.footer.kosherRabbi}<br/>{t.footer.kosherOrg}
                 </p>
            </div>

        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-gray-600">
            <div className="flex flex-wrap justify-center md:justify-start gap-4">
                <p>© {new Date().getFullYear()} {language === 'he' ? 'יהודה פייט' : 'Yehuda Fait'}. {t.footer.rights}</p>
                {/* Removed 'underline decoration-fait-gold/30' classes below */}
                <Link to="/accessibility" className="hover:text-fait-gold transition-colors">
                    {t.footer.accessibility}
                </Link>
            </div>
            <div className="flex items-center gap-6">
                 <span>{language === 'he' ? 'כשר למהדרין' : 'Kosher Lemehadrin'}</span>
                 <span className="w-1 h-1 rounded-full bg-gray-700"></span>
                 <span>Bnei Brak</span>
            </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;
